import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useBackgroundJobs } from '../../contexts/BackgroundJobsContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CalendarClock, AlertTriangle, Landmark } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const month = (iso) => (iso ? new Date(`${iso.slice(0, 7)}-01T00:00:00`).toLocaleString(undefined, { month: 'long', year: 'numeric' }) : '—');
const money = (v) => (v == null ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Modal 1 · keep (recommended, non-destructive) · move · or cancel the upload altogether
const OfferStep = ({ offer, onKeep, onMove, onCancel, busy }) => (
  <>
    <DialogHeader>
      <DialogTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5" />This statement goes back further than your start date</DialogTitle>
      <DialogDescription>
        <strong>{offer.filename}</strong> has transactions from <strong>{offer.first_row_date}</strong>, but your accounting start is <strong>{month(offer.current_start)}</strong>.
        We can anchor your books from <strong>{month(offer.proposed_start)}</strong> instead.
      </DialogDescription>
    </DialogHeader>
    <div className="grid gap-3 md:grid-cols-2 text-sm">
      <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3" data-testid="start-date-keep-option">
        <p className="font-medium">Keep {month(offer.current_start)} <Badge variant="outline" className="ml-1">Recommended</Badge></p>
        <p className="text-muted-foreground mt-1">
          Only transactions from {offer.current_start} onwards are brought in; the {offer.rows_discarded_if_keep} earlier row(s) are left out.
          Your opening balance on {offer.current_start} is checked against this statement's running balance and corrected if it differs.
        </p>
      </div>
      <div className="rounded-lg border p-3" data-testid="start-date-move-option">
        <p className="font-medium">Move back to {month(offer.proposed_start)} <Badge variant="outline" className="ml-1">+{offer.months_gained} month(s)</Badge></p>
        <p className="text-muted-foreground mt-1">
          Opening balance on {offer.opening_balance_date}: <strong>{money(offer.opening_balance_at_start)}</strong>
          {offer.account_opened_in_statement ? ' (account opened in this statement)' : ` — proven from the statement (${offer.ob_method}${offer.ob_reconciled ? ', every balance reconciles' : ''})`}.
        </p>
      </div>
    </div>
    <DialogFooter className="gap-2 sm:justify-between">
      <Button variant="ghost" disabled={busy} onClick={onCancel} className="text-muted-foreground" data-testid="start-date-cancel-upload-btn">Cancel this upload</Button>
      <div className="flex flex-wrap gap-2 justify-end">
        <Button variant="outline" disabled={busy} onClick={onMove} data-testid="start-date-move-btn">Move back to {month(offer.proposed_start)}</Button>
        <Button disabled={busy} onClick={onKeep} data-testid="start-date-keep-btn">Keep {month(offer.current_start)}</Button>
      </div>
    </DialogFooter>
  </>
);

// Shared impact review body (upload-driven Modal 2 AND Settings → change start date). `onChange` reports
// { confirmed, backlog } so the host decides when its destructive button unlocks.
export const StartDateImpactReview = ({ impact, onChange, anchorText }) => {
  const [c1, setC1] = useState(false); const [c2, setC2] = useState(false); const [c3, setC3] = useState(false);
  const [backlog, setBacklog] = useState('free');
  const ob = impact.opening_balances || {};
  const needOb = ob.any;
  useEffect(() => { onChange?.({ confirmed: c1 && c2 && c3, backlog }); }, [c1, c2, c3, backlog]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
      <div className="space-y-3 text-sm max-h-[50vh] overflow-y-auto pr-1" data-testid="start-date-impact-review">
        <div className="rounded-lg border p-3" data-testid="impact-gaps">
          <p className="font-medium flex items-center gap-1"><Landmark className="h-4 w-4" />Banks that will need statements for the new period ({impact.banks_with_gap.length})</p>
          {impact.banks_with_gap.length === 0 ? <p className="text-muted-foreground">No other bank needs anything.</p> : (
            <ul className="list-disc pl-5">{impact.banks_with_gap.map((b) => (
              <li key={b.bank_account_id}>{b.bank_name}{b.account_number ? ` ····${b.account_number}` : ''}: {b.gap_start} → {b.gap_end} — {b.action}</li>
            ))}</ul>
          )}
          {impact.banks_with_gap.length > 0 && <p className="text-xs text-muted-foreground mt-1">We will e-mail you a reminder with a direct upload link for each one.</p>}
        </div>
        <div className="rounded-lg border p-3" data-testid="impact-opening-balances">
          <p className="font-medium">Opening balances that will be removed and rebuilt</p>
          {needOb ? (
            <ul className="list-disc pl-5">
              {ob.statement_derived > 0 && <li>{ob.statement_derived} bank opening balance(s) derived from statements</li>}
              {ob.provider_derived > 0 && <li>{ob.provider_derived} bank opening balance(s) pulled from a bank connection</li>}
              {ob.manual_or_wizard_uploads > 0 && <li>{ob.manual_or_wizard_uploads} manually entered / wizard opening balance set(s)</li>}
              {ob.breakdowns > 0 && <li>{ob.breakdowns} Breakdown Center item(s){ob.breakdowns_unfinished > 0 ? ` (${ob.breakdowns_unfinished} unfinished)` : ''}</li>}
              {ob.journals > 0 && <li>{ob.journals} opening-balance journal(s)</li>}
              {ob.documents > 0 && <li data-testid="impact-ob-documents">{ob.documents} document(s) created from opening balances (assets, loans, prepayments, opening invoices/bills)</li>}
              {(ob.follow_on_journals > 0 || ob.follow_on_schedules > 0) && <li data-testid="impact-follow-on">{ob.follow_on_journals} follow-on journal(s) and {ob.follow_on_schedules} schedule(s) built on them (depreciation, amortisation, interest, recognition)</li>}
              {ob.reconciliation_matches > 0 && <li data-testid="impact-recon">{ob.reconciliation_matches} bank transaction(s) matched to those journals will go back to review</li>}
            </ul>
          ) : <p className="text-muted-foreground">No opening balances recorded yet — nothing to rebuild.</p>}
          <ul className="list-disc pl-5 text-muted-foreground mt-1">{impact.deletes.map((d) => <li key={d}>{d}</li>)}</ul>
        </div>
        {impact.backlog_choice_required && (
          <div className="rounded-lg border p-3" data-testid="impact-backlog">
            <p className="font-medium">History before your registration month ({impact.registration_month})</p>
            <label className="flex items-center gap-2 mt-1"><input type="radio" name="bl" checked={backlog === 'free'} onChange={() => setBacklog('free')} />Free — deterministic categorisation</label>
            <label className="flex items-center gap-2"><input type="radio" name="bl" checked={backlog === 'paid'} onChange={() => setBacklog('paid')} />Paid — AI-assisted categorisation (quoted before charging)</label>
          </div>
        )}
        <label className="flex items-start gap-2"><input type="checkbox" checked={c1} onChange={(e) => setC1(e.target.checked)} data-testid="impact-confirm-gaps" />
          {impact.banks_with_gap.length > 0
            ? 'I will upload statements covering the new period for the banks listed above (or restore them from the bank feed where available).'
            : (anchorText || `I understand my books now start from ${month(impact.proposed_start)}; any bank I add must have statements from that date.`)}</label>
        <label className="flex items-start gap-2"><input type="checkbox" checked={c2} onChange={(e) => setC2(e.target.checked)} data-testid="impact-confirm-ob" />I understand my existing opening balances, their journals and Breakdown Center treatments will be deleted and must be redone from {month(impact.proposed_start)}.</label>
        <label className="flex items-start gap-2"><input type="checkbox" checked={c3} onChange={(e) => setC3(e.target.checked)} data-testid="impact-confirm-cascade" />I understand follow-on journals, schedules and reconciliation matches built on them will be removed too.</label>
      </div>
  );
};

// Modal 2 · impact review + confirmations
const ImpactStep = ({ offer, impact, onBack, onKeep, onCancel, onConfirm, busy }) => {
  const [state, setState] = useState({ confirmed: false, backlog: 'free' });
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" />Before we move your start to {month(impact.proposed_start)}</DialogTitle>
        <DialogDescription>Please read and confirm each point. This cannot be undone automatically.</DialogDescription>
      </DialogHeader>
      <StartDateImpactReview impact={impact} onChange={setState}
        anchorText={`I understand this statement anchors my books from ${month(impact.proposed_start)}; any other bank I add must have statements from that date.`} />
      <DialogFooter className="gap-2 sm:justify-between">
        <div className="flex gap-2">
          <Button variant="outline" disabled={busy} onClick={onBack} data-testid="impact-back-btn">Back</Button>
          <Button variant="ghost" disabled={busy} onClick={onCancel} className="text-muted-foreground" data-testid="impact-cancel-upload-btn">Cancel upload</Button>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="outline" disabled={busy} onClick={onKeep} data-testid="impact-keep-btn">Don't move — keep {month(impact.current_start || offer.current_start)}</Button>
          <Button variant="destructive" disabled={busy || !state.confirmed} onClick={() => onConfirm(state.backlog)} data-testid="impact-confirm-btn">Move start date and continue upload</Button>
        </div>
      </DialogFooter>
    </>
  );
};

export default function GlobalStartDateDecisionProvider() {
  const { jobs, addJob } = useBackgroundJobs();
  const [job, setJob] = useState(null);
  const [data, setData] = useState(null);
  const [step, setStep] = useState('offer');
  const [busy, setBusy] = useState(false);
  const [handled, setHandled] = useState(() => new Set());

  useEffect(() => {
    const next = (jobs || []).find((j) => j.status === 'awaiting_start_date_decision' && !handled.has(j.jobId));
    if (!next || job) return;
    setJob(next);
    axios.get(`${API}/bank-feeds/upload-jobs/${next.jobId}/start-date-offer`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => { toast.error('Could not load the start-date offer'); setJob(null); });
  }, [jobs, job, handled]);

  const finish = useCallback(() => { if (job) setHandled((h) => new Set(h).add(job.jobId)); setJob(null); setData(null); setStep('offer'); }, [job]);

  const decide = async (decision, extra = {}) => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/bank-feeds/upload-jobs/${job.jobId}/start-date-decision`, { decision, ...extra }, { withCredentials: true });
      toast.success(decision === 'move' ? `Start date moved to ${month(r.accounting_start_date)} — continuing your upload` : 'Keeping your start date — continuing your upload');
      addJob({ jobId: job.jobId, filename: job.filename, status: 'queued' });
      finish();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Could not apply your decision');
    } finally { setBusy(false); }
  };

  const cancelUpload = () => {
    // the cancel endpoint does its cleanup for a few seconds — close immediately, report in the background
    const id = job.jobId;
    finish();
    axios.post(`${API}/bank-feeds/upload-jobs/${id}/cancel`, {}, { withCredentials: true })
      .then(() => toast.success('Upload cancelled — nothing was changed'))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not cancel the upload'));
  };

  if (!job || !data) return null;
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl [&>button]:hidden" data-testid="start-date-decision-dialog">
        {step === 'offer' ? (
          <OfferStep offer={data.offer} busy={busy} onKeep={() => decide('keep')} onMove={() => setStep('impact')} onCancel={cancelUpload} />
        ) : (
          <ImpactStep offer={data.offer} impact={data.impact} busy={busy} onBack={() => setStep('offer')} onKeep={() => decide('keep')} onCancel={cancelUpload}
            onConfirm={(backlog) => decide('move', { confirm_gap_uploads: true, confirm_opening_balance_deletion: true, confirm_transaction_handling: true,
              backlog_ai_choice: data.impact.backlog_choice_required ? backlog : null })} />
        )}
      </DialogContent>
    </Dialog>
  );
}
